import React from 'react';

const Chapter2Content = () => {
  return (
    <article className="prose prose-lg max-w-none">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-2">
          Chapter Two
        </h1>
        <p className="text-xl text-gray-600">
          The Architecture of Revelation
        </p>
        <p className="text-lg text-gray-500 mt-2">
          How the Last Book Is Put Together
        </p>
      </div>

      {/* Opening */}
      <div className="mb-12">
        <p className="text-gray-800 leading-relaxed mb-4">
          Revelation can feel like the most confusing book in the Bible. Angels, beasts, dragons, numbers, symbols — what does any of it mean, and what order does it go in?
        </p>
        <p className="text-gray-800 leading-relaxed mb-4">
          Here is the key that unlocks it.
        </p>
        <div className="flex justify-center gap-4 my-8">
          <span className="text-2xl text-amber-600">✦</span>
          <span className="text-2xl text-amber-600">✦</span>
          <span className="text-2xl text-amber-600">✦</span>
        </div>
      </div>

      {/* What is Recapitulation */}
      <div className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          WHAT IS RECAPITULATION?
        </h2>
        <p className="text-gray-800 leading-relaxed mb-4">
          Recapitulation is a long word for a simple idea: <strong>telling the same story from different angles.</strong>
        </p>
        <p className="text-gray-800 leading-relaxed mb-4">
          You do this all the time. Something extraordinary happened at school. At dinner you tell the whole story — beginning, middle, end. Then your best friend calls, and you tell it again, but you focus on the most dramatic moment. Then your grandmother calls, and you slow it down and give her every detail. Same event. Three tellings. Each one true. Each one adds something.
        </p>
        
        <div className="bg-amber-50 border-l-4 border-amber-600 p-6 my-8">
          <h3 className="font-bold text-amber-900 mb-3">What recapitulation means:</h3>
          <p className="text-amber-900 italic">
            The structured repetition of an earlier story — told again from a new angle — in order to add depth, confirm the truth, and reveal the full pattern.
          </p>
          <p className="text-amber-900 mt-4">
            <strong>Repetition is not the author making a mistake.<br />
            Repetition is the author building a case.</strong>
          </p>
        </div>

        <p className="text-gray-800 leading-relaxed mb-4">
          In a courtroom, one witness states the facts. A second witness says the same thing from a different vantage point. A third witness confirms it from yet another angle. No one calls that a contradiction. Everyone calls that a conviction.
        </p>
        <p className="text-gray-800 leading-relaxed mb-4">
          <strong>That is exactly what God does in Revelation.</strong>
        </p>
      </div>

      {/* Biblical Examples */}
      <div className="mb-12">
        <p className="text-gray-800 leading-relaxed mb-4">
          The Bible does this constantly. Genesis 1 tells the whole creation story in order. Genesis 2 goes back to day six and retells the creation of Adam and Eve in detail — same event, different angle. The four Gospels all tell the life, death, and resurrection of Jesus — same story, four windows.
        </p>
        <p className="text-gray-800 leading-relaxed mb-4">
          You know this storytelling technique from the best novels and films ever made. Directors call it a <em>narrative reprise</em>. Novelists call it a <em>structural echo</em>. Ancient teachers called it <em>spiral teaching</em> — returning to the same truth at a deeper level every time. The Bible has used it from the very first page.
        </p>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 my-8">
          <h3 className="font-bold text-blue-900 mb-3">The name scholars gave it:</h3>
          <p className="text-blue-900 mb-2">
            <strong>Recapitulation.</strong> From the Latin <em>recapitulatio</em> — "to go through the main points again."
          </p>
          <p className="text-blue-900">
            In Greek, the word is ἀνακεφαλαίωσις — <em>anakephalaiosis</em>.<br />
            It means: <strong>to sum up again from the beginning.</strong>
          </p>
        </div>
      </div>

      {/* The Evidence Table */}
      <div className="my-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          HERE IS THE EVIDENCE
        </h2>
        <p className="text-gray-800 leading-relaxed mb-6">
          Revelation recapitulates with the 7 seals, the 7 trumpets, and the 7 bowls. They are not three different sets of events marching in strict sequence. They are three tellings of the same events from three different perspectives — like three camera angles on the same scene.
        </p>

        <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-300 rounded-lg p-6 mb-8">
          <h3 className="text-xl font-bold text-center text-amber-900 mb-6">
            The Three Sequences Side by Side
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-amber-100">
                  <th className="p-3 text-left font-bold">Position</th>
                  <th className="p-3 text-left font-bold">The Seals</th>
                  <th className="p-3 text-left font-bold">The Trumpets</th>
                  <th className="p-3 text-left font-bold">The Bowls</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-amber-200">
                  <td className="p-3 font-semibold">1st</td>
                  <td className="p-3">Conquest goes forth</td>
                  <td className="p-3">Hail, fire, blood — earth struck</td>
                  <td className="p-3">Sores on those with the mark</td>
                </tr>
                <tr className="border-t border-amber-200 bg-amber-50">
                  <td className="p-3 font-semibold">2nd</td>
                  <td className="p-3">War — peace taken</td>
                  <td className="p-3">Sea becomes blood (1/3)</td>
                  <td className="p-3">Sea becomes blood (all)</td>
                </tr>
                <tr className="border-t border-amber-200">
                  <td className="p-3 font-semibold">3rd</td>
                  <td className="p-3">Famine — scarcity</td>
                  <td className="p-3">Waters made bitter</td>
                  <td className="p-3">Rivers/springs become blood</td>
                </tr>
                <tr className="border-t border-amber-200 bg-amber-50">
                  <td className="p-3 font-semibold">4th</td>
                  <td className="p-3">Death — 1/4 of earth</td>
                  <td className="p-3">Sun, moon, stars darkened</td>
                  <td className="p-3">Sun scorches with fire</td>
                </tr>
                <tr className="border-t border-amber-200">
                  <td className="p-3 font-semibold">5th</td>
                  <td className="p-3">Martyrs cry for justice</td>
                  <td className="p-3">Torment from the abyss</td>
                  <td className="p-3">Darkness on beast's kingdom</td>
                </tr>
                <tr className="border-t border-amber-200 bg-amber-50">
                  <td className="p-3 font-semibold">6th</td>
                  <td className="p-3">Cosmic signs — armies</td>
                  <td className="p-3">Euphrates: 200 million army</td>
                  <td className="p-3">Euphrates dried — Armageddon</td>
                </tr>
                <tr className="border-t border-amber-200 bg-green-50">
                  <td className="p-3 font-semibold">7th</td>
                  <td className="p-3">Silence / worship</td>
                  <td className="p-3">"Kingdoms are the Lord's"</td>
                  <td className="p-3">"It is done" — earthquake</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-red-50 border-l-4 border-red-600 p-6 my-8">
          <h3 className="font-bold text-red-900 mb-3">A Problem the Linear Reading Cannot Solve</h3>
          <p className="text-red-900 mb-4">
            <strong>Revelation 6:12</strong> — the sixth seal: "The sun became black as sackcloth."
          </p>
          <p className="text-red-900 mb-4">
            The sun goes black. Gone.
          </p>
          <p className="text-red-900 mb-4">
            <strong>Revelation 16:8</strong> — the fourth bowl: "Power was given unto him to scorch men with fire."
          </p>
          <p className="text-red-900 mb-4">
            <strong>A sun that has been turned black as sackcloth cannot scorch anyone with fire.</strong>
          </p>
          <p className="text-red-900">
            The linear reading has created a contradiction inside its own rules — and it has no way to fix it.
          </p>
        </div>

        <p className="text-gray-800 leading-relaxed mb-4">
          <strong>Recapitulation fixes it instantly.</strong> If the seals and the bowls are not two separate sequential series — if they are two different camera angles on the same tribulation period — then the darkened sun and the scorching sun are two descriptions of the same cosmic disruption, seen from two different vantage points.
        </p>
      </div>

      {/* The Seventh Position */}
      <div className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          The Seventh Position: Three Endings, One Moment
        </h2>

        <div className="space-y-4 mb-8">
          <div className="bg-green-50 border-l-4 border-green-600 p-4">
            <h4 className="font-bold text-green-900">Seventh Seal (Rev 8:1, 5)</h4>
            <p className="text-green-900">Silence in heaven. Then thunder, lightning, and earthquake.</p>
          </div>
          <div className="bg-green-50 border-l-4 border-green-600 p-4">
            <h4 className="font-bold text-green-900">Seventh Trumpet (Rev 11:15, 19)</h4>
            <p className="text-green-900">"The kingdoms of this world have become the kingdoms of our Lord." Lightning, thunder, earthquake, hail.</p>
          </div>
          <div className="bg-green-50 border-l-4 border-green-600 p-4">
            <h4 className="font-bold text-green-900">Seventh Bowl (Rev 16:17–18)</h4>
            <p className="text-green-900">"It is done!" Greatest earthquake in history. Lightning, thunder.</p>
          </div>
        </div>

        <p className="text-gray-800 leading-relaxed mb-4">
          <strong>Three endings. Three times. Same moment. The end. The completion.</strong>
        </p>
        <p className="text-gray-800 leading-relaxed mb-4">
          The seventh trumpet is the last trumpet. And all three say the same thing: <em>it is finished.</em>
        </p>
      </div>

      {/* Conclusion */}
      <div className="text-center my-12">
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-300 rounded-lg p-8 inline-block">
          <p className="text-lg font-bold text-gray-900">
            The seventh seal. The seventh trumpet. The seventh bowl.
          </p>
          <p className="text-lg text-gray-800 mt-2">
            Three sequences. Three camera angles. One moment. One King. One return.
          </p>
          <p className="text-gray-700 mt-4 italic">
            God said it once. Then He said it again so we would not miss it.<br />
            Then He said it a third time so we would never forget it.
          </p>
        </div>
      </div>

      <div className="flex justify-center gap-4 my-8">
        <span className="text-2xl text-amber-600">✦</span>
        <span className="text-2xl text-amber-600">✦</span>
        <span className="text-2xl text-amber-600">✦</span>
      </div>
    </article>
  );
};

export default Chapter2Content;
